import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import routes from '@routes/routes'
import Tag from '@utils/Tag'
import {
  marginAcceptRequest,
  marginResultRequest,
} from '@store/refinanciamento/marginAcceptResult/actions'
import { MarginAcceptApiErrorCodes } from '@store/refinanciamento/marginAcceptResult/types'
import {
  simulacaoRequest,
  trackingRefin,
  setBottomsheetOrigin,
  setParcelaSelecionada,
  setTotalParcelas,
} from '@store/refinanciamento/simulacao/actions'
import { CallbackSimulacaoType, InfoParcelas } from '@store/refinanciamento/simulacao/types'
import { ConveniosCodes, MarginErrorCodes, StepRefin, PendenciaEnumV3 } from '@utils/enums'
import { getParcelaAtualSimulacoes } from '@utils/functions'
import { ApplicationState } from '@store/types'
import { BridgeService } from '@services/bridge'
import { useTrackingRefin } from '@hooks'

import CarregamentoLayout from './Layout'
import { enumLoading, loadingSteps, LoadingStep } from './utils'
import { FirstAttemptBottomSheet } from './SiapeAuthorization/FirstAttemptBottomSheet'
import { SecondAttemptBottomSheet } from './SiapeAuthorization/SecondAttemptBottomSheet'

interface MarginResultParams {
  trackingName: string
  trackingAction: string
  retryFunction: () => void
  hasInssSpecificPendencies?: boolean
  hasInsuranceOffer?: boolean
}

type SiapeAuthorizationGate = 'first' | 'second' | null

const CarregamentoSimulacao: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useTrackingRefin(StepRefin.CARREGAMENTO_SIMULACAO)

  const isApp = useSelector((state: ApplicationState) => state.ui.navigation.isApp)
  const convenioCodigo = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos.convenioCodigo,
  )
  const bottomsheetOrigin = useSelector(
    (state: ApplicationState) => state.refinanciamento.simulacao.bottomsheetOrigin,
  )
  const convenioName = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos.convenioName,
  )
  const contratosSelecionados = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos.contratosSelecionados,
  )

  const [currentLoadingStep, setCurrentLoadingStep] = useState<LoadingStep>(enumLoading.initial)
  const [siapeAuthorizationGate, setSiapeAuthorizationGate] = useState<SiapeAuthorizationGate>(null)
  const [marginQueryCycle, setMarginQueryCycle] = useState(0)

  const enableInsuranceFeatureINSS = process.env.REACT_APP_ENABLE_SEGURO_FEATURE_INSS === 'true'
  const enableInsuranceFeatureSIAPE = process.env.REACT_APP_ENABLE_SEGURO_FEATURE_SIAPE === 'true'
  const forbiddenCovenantsForInsurance = [
    ConveniosCodes.INSS,
    ConveniosCodes.PREF_SP,
    ConveniosCodes.ESTADO_SP,
  ]
  const allowedCovenantForInsurance = !forbiddenCovenantsForInsurance.includes(convenioCodigo)

  const applyDefaultSelectionAndProceed = useCallback(
    ({
      simulations,
      simulationsWithInsurance,
    }: {
      simulations: InfoParcelas[]
      simulationsWithInsurance: InfoParcelas[]
    }) => {
      const sourceList = simulations?.length > 0 ? simulations : simulationsWithInsurance
      const current = getParcelaAtualSimulacoes(sourceList)
      dispatch(setParcelaSelecionada(current))
      dispatch(setTotalParcelas(current.numberOfInstallments))
      dispatch(trackingRefin(StepRefin.CONTRATAR_SIMULACAO_REFIN))
      navigate(routes.RESULTADO_SIMULACAO)
    },
    [dispatch, navigate],
  )

  const lembreteKey = 'lembreteCarregamento1Count'

  const MAX_RETRIES = 3
  const RETRY_DELAY = 5000
  const [retryCount, setRetryCount] = useState(0)

  const lembrete = useMemo(
    () =>
      currentLoadingStep === enumLoading.initial || currentLoadingStep === enumLoading.secondStep
        ? 'Lembre-se: o Inter não trabalha com intermediários'
        : 'Lembre-se: não solicitamos a devolução do valor depositado',
    [currentLoadingStep],
  )

  const handleInssSpecificPendencies = useCallback(
    ({
      pending,
      simulations,
      simulationsWithInsurance,
      hasInsuranceOffer,
    }: {
      pending: string | undefined
      simulations: InfoParcelas[]
      simulationsWithInsurance: InfoParcelas[]
      hasInsuranceOffer: boolean
    }) => {
      if (pending === PendenciaEnumV3.PENDENCIA_REVERSIVEL_LEAD_NOVO) {
        navigate(routes.INSS_BLOQUEADO)
        return
      }

      if (pending === PendenciaEnumV3.PENDENCIA_TEIMOSINHA) {
        navigate(routes.INDISPONIVEL)
        return
      }

      if (hasInsuranceOffer && simulationsWithInsurance?.length > 0) {
        if (enableInsuranceFeatureINSS) {
          navigate(routes.OFERTA_SEGURO)
          return
        }
        applyDefaultSelectionAndProceed({
          simulations,
          simulationsWithInsurance,
        })
        return
      }

      if (simulations?.length > 0) {
        navigate(routes.RESULTADO_SIMULACAO)
        return
      }

      navigate(routes.ERRO, {
        state: {
          convenioNameLocation: convenioName,
          step: 'carregamento-dados-1',
        },
      })
    },
    [navigate, applyDefaultSelectionAndProceed, enableInsuranceFeatureINSS, convenioName],
  )

  const handleSimulationCallback = useCallback(
    ({
      pending,
      simulations,
      simulationsWithInsurance,
      hasInssSpecificPendencies,
      hasInsuranceOffer,
      retryFunction,
    }: {
      pending: string | undefined
      simulations: InfoParcelas[]
      simulationsWithInsurance: InfoParcelas[]
      hasInssSpecificPendencies: boolean
      hasInsuranceOffer: boolean
      retryFunction: () => void
    }) => {
      if (pending === PendenciaEnumV3.PENDENCIA_VALIDACAO && retryCount < MAX_RETRIES) {
        setRetryCount((prev) => prev + 1)
        setTimeout(() => {
          retryFunction()
        }, RETRY_DELAY)
        return
      }

      if (
        pending === PendenciaEnumV3.PENDENCIA_IRREVERSIVEL ||
        pending === MarginErrorCodes.DISQUALIFIED_CROSS_CS005
      ) {
        navigate(routes.DESQUALIFICACAO, {
          state: {
            convenioNameLocation: convenioName,
            step: 'carregamento-dados-1',
            content: 'FGTS',
          },
        })
        return
      }

      if (pending === MarginErrorCodes.DISQUALIFIED_CROSS_CS006) {
        navigate(routes.DESQUALIFICACAO, {
          state: {
            convenioNameLocation: convenioName,
            step: 'carregamento-dados-1',
            content: 'ACCOUNT',
          },
        })
        return
      }

      if (hasInssSpecificPendencies) {
        handleInssSpecificPendencies({
          pending,
          simulations,
          simulationsWithInsurance,
          hasInsuranceOffer,
        })
        return
      }

      if (hasInsuranceOffer && simulationsWithInsurance?.length > 0) {
        dispatch(setBottomsheetOrigin(false))
        if (
          (enableInsuranceFeatureINSS && allowedCovenantForInsurance) ||
          (enableInsuranceFeatureSIAPE && allowedCovenantForInsurance)
        ) {
          navigate(routes.OFERTA_SEGURO)
          return
        }
        applyDefaultSelectionAndProceed({
          simulations,
          simulationsWithInsurance,
        })
        return
      }

      if (simulations?.length > 0) {
        dispatch(setBottomsheetOrigin(false))
        navigate(routes.RESULTADO_SIMULACAO)
        return
      }

      dispatch(setBottomsheetOrigin(false))
      navigate(routes.ERRO, {
        state: {
          convenioNameLocation: convenioName,
          step: 'carregamento-dados-1',
        },
      })
    },
    [
      retryCount,
      navigate,
      handleInssSpecificPendencies,
      dispatch,
      applyDefaultSelectionAndProceed,
      enableInsuranceFeatureINSS,
      enableInsuranceFeatureSIAPE,
      allowedCovenantForInsurance,
      convenioName,
    ],
  )

  const handleMarginResult = useCallback(
    (params: MarginResultParams): void => {
      const {
        trackingName,
        trackingAction,
        retryFunction,
        hasInssSpecificPendencies = false,
        hasInsuranceOffer = false,
      } = params

      BridgeService.tic({
        name: trackingName,
        action: trackingAction,
      })

      const onSuccess = (code: string) => {
        if (
          code === MarginErrorCodes.DISQUALIFIED ||
          code === MarginErrorCodes.DISQUALIFIED_CROSS_CS002
        ) {
          navigate(routes.DESQUALIFICACAO, {
            state: {
              convenioNameLocation: convenioName,
              step: 'carregamento-dados-1',
              content: 'FGTS',
            },
          })
          return
        }

        if (code === MarginErrorCodes.DISQUALIFIED_CROSS_CS003) {
          navigate(routes.DESQUALIFICACAO, {
            state: {
              convenioNameLocation: convenioName,
              step: 'carregamento-dados-1',
              content: 'ACCOUNT',
            },
          })
          return
        }

        if (code === MarginErrorCodes.ERROR) {
          navigate(routes.ERRO, {
            state: {
              convenioNameLocation: convenioName,
              step: 'carregamento-dados-1',
            },
          })
          return
        }

        if (code === MarginErrorCodes.PROCESSAMENTO_VIRADA_COMPETENCIA) {
          navigate(routes.SERVICO_INDISPONIVEL)
          return
        }

        if (code === MarginErrorCodes.ERROR_INSS_INDISPONIVEL) {
          navigate(routes.INSS_INDISPONIVEL)
          return
        }

        if (code === MarginErrorCodes.INSS_BLOQUEADO) {
          navigate(routes.INSS_BLOQUEADO)
          return
        }

        if (convenioCodigo === ConveniosCodes.SIAPE_SERVIDOR_FEDERAL) {
          if (code === MarginErrorCodes.FIRST_RETRY_AUTH_SIAPE) {
            setSiapeAuthorizationGate('first')
            return
          }

          if (code === MarginErrorCodes.SECOND_RETRY_AUTH_SIAPE) {
            setSiapeAuthorizationGate('second')
            return
          }
        }

        dispatch(
          simulacaoRequest({
            callback: ({
              pending,
              simulations,
              simulationsWithInsurance,
            }: CallbackSimulacaoType) => {
              dispatch(trackingRefin(StepRefin.DOCUMENTACAO_AUTORIZADA))
              dispatch(trackingRefin(StepRefin.SIMULAR_CONTRATOS))

              handleSimulationCallback({
                pending,
                simulations,
                simulationsWithInsurance,
                hasInssSpecificPendencies,
                hasInsuranceOffer,
                retryFunction,
              })
            },
            erroCallback: () => {
              navigate(routes.ERRO, {
                state: {
                  convenioNameLocation: convenioName,
                  step: 'carregamento-dados-1',
                },
              })
            },
          }),
        )
      }

      dispatch(
        marginResultRequest({
          convenioName,
          callback: onSuccess,
        }),
      )
    },
    [dispatch, navigate, handleSimulationCallback, convenioName, convenioCodigo],
  )

  const callMarginResultINSS = useCallback(
    (): void =>
      handleMarginResult({
        trackingName: 'btn_continuar_inss',
        trackingAction: 'retornar_margin_result_inss',
        retryFunction: () => callMarginResultINSS(),
        hasInssSpecificPendencies: true,
        hasInsuranceOffer: true,
      }),
    [handleMarginResult],
  )

  const callMarginResultCTPS = useCallback(
    (): void =>
      handleMarginResult({
        trackingName: 'btn_continuar_ctps',
        trackingAction: 'retornar_margin_result_ctps',
        retryFunction: () => callMarginResultCTPS(),
        hasInssSpecificPendencies: false,
        hasInsuranceOffer: false,
      }),
    [handleMarginResult],
  )

  const callMarginResultOthers = useCallback(
    (): void =>
      handleMarginResult({
        trackingName: `btn_continuar_${convenioName.toLowerCase()}`,
        trackingAction: `retornar_margin_result_${convenioName.toLowerCase()}`,
        retryFunction: () => callMarginResultOthers(),
        hasInssSpecificPendencies: false,
        hasInsuranceOffer: true,
      }),
    [handleMarginResult, convenioName],
  )

  const restartMarginQueryLoading = useCallback((): void => {
    setCurrentLoadingStep(enumLoading.initial)
    setMarginQueryCycle((cycle) => cycle + 1)
  }, [])

  const handleSiapeMarginAcceptRetry = useCallback((): void => {
    dispatch(
      marginAcceptRequest({
        convenioName,
        selectedContracts: contratosSelecionados.map((contract) => contract.numeroContrato),
        errorCallback: (errorCode) => {
          if (errorCode === MarginAcceptApiErrorCodes.PROCESSAMENTO_VIRADA_COMPETENCIA) {
            navigate(routes.SERVICO_INDISPONIVEL)
          }
        },
        callback: restartMarginQueryLoading,
      }),
    )
  }, [contratosSelecionados, convenioName, dispatch, navigate, restartMarginQueryLoading])

  const handleConfirmSiapeAuthorization = useCallback((): void => {
    setSiapeAuthorizationGate(null)
  }, [])

  useEffect(() => {
    const saved = localStorage.getItem(lembreteKey)
    const currentCount = saved ? Number(saved) : 0
    const newCount = currentCount + 1
    localStorage.setItem(lembreteKey, String(newCount))
    BridgeService.tac({
      name: 'carregamento_dados',
      actions: ['buscar_documentos', 'criar_proposta'],
    })
  }, [lembreteKey])

  useEffect(() => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:loading',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Carregamento de dados',
        section: 'loading',
        sub_section: '',
        step: '',
        scenario: 'completion',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Carregamento de dados',
        c_page: window.location.href,
        redirect_url:
          (enableInsuranceFeatureINSS || enableInsuranceFeatureSIAPE) && allowedCovenantForInsurance
            ? routes.OFERTA_SEGURO
            : routes.RESULTADO_SIMULACAO,
        section_name: 'payrollloan:refin:loading',
        section: 'loading',
        sub_section: '',
        step: '',
        scenario: 'completion',
      })
    }
  }, [isApp, allowedCovenantForInsurance, enableInsuranceFeatureINSS, enableInsuranceFeatureSIAPE])

  useEffect(() => {
    const timers = loadingSteps.map(({ step, delay }) =>
      setTimeout(() => setCurrentLoadingStep(step), delay),
    )

    const finalTimeout = setTimeout(() => {
      if (convenioCodigo === ConveniosCodes.INSS && bottomsheetOrigin) {
        callMarginResultINSS()
        return
      }
      if (convenioCodigo === ConveniosCodes.CTPS && bottomsheetOrigin) {
        callMarginResultCTPS()
        return
      }
      if (convenioCodigo !== ConveniosCodes.INSS && convenioCodigo !== ConveniosCodes.CTPS) {
        callMarginResultOthers()
      }
    }, 11000)

    return () => {
      timers.forEach(clearTimeout)
      clearTimeout(finalTimeout)
    }
  }, [
    dispatch,
    navigate,
    convenioCodigo,
    bottomsheetOrigin,
    callMarginResultINSS,
    callMarginResultCTPS,
    callMarginResultOthers,
    isApp,
    convenioName,
    marginQueryCycle,
  ])

  return (
    <>
      <CarregamentoLayout
        lembrete={lembrete}
        size={currentLoadingStep.size}
        backgroundColor={currentLoadingStep.bgColor}
      />
      {siapeAuthorizationGate === 'first' ? (
        <FirstAttemptBottomSheet
          onDismiss={() => undefined}
          onConfirmAuthorization={handleConfirmSiapeAuthorization}
          onRetryMarginResult={handleSiapeMarginAcceptRetry}
        />
      ) : null}
      {siapeAuthorizationGate === 'second' ? (
        <SecondAttemptBottomSheet
          onDismiss={() => undefined}
          onConfirmAuthorization={handleConfirmSiapeAuthorization}
          onRetryMarginResult={handleSiapeMarginAcceptRetry}
        />
      ) : null}
    </>
  )
}

export default CarregamentoSimulacao
